import React, { useState, useRef, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PayPalButtons,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import OrderSummary from "./OrderSummary";
import {
  createGuestOrder,
  createOrder,
  getOrderById,
  orderCallback,
  paypalPaymentCallback,
} from "../../services/order.service";
import { errorToast, successToast } from "../../utils/Toast";
import { clearLocalCart, getLocalCart } from "../../services/localCart";
import { getToken } from "../../services/User.service";
import { CartCountContext } from "../../App";
import { images } from "../Utility/Images";

const PAYPAL_SUPPORTED_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "AUD",
  "CAD",
  "JPY",
  "SGD",
  "NZD",
  "HKD",
  "SEK",
  "CHF",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "ILS",
  "MXN",
  "PHP",
  "TWD",
  "THB",
  "BRL",
  "MYR",
];

function Payment({ setprocedure }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("GBP");
  const [, setCarts] = useContext(CartCountContext);

  useEffect(() => {
    try {
      let localCurrency = localStorage.getItem("currency-conver-rate");
      if (localCurrency) {
        let localCurrencyObj = JSON.parse(localCurrency);
        if (localCurrencyObj && localCurrencyObj?.code) {
          setCurrency(localCurrencyObj.code);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handlecreateGuestOrder = async () => {
    try {
      let localCartRes = getLocalCart();
      let cartItems = localCartRes?.data?.data || [];
      if (!cartItems || cartItems.length === 0) {
        errorToast("Your Cart is Empty");
        return null;
      }
      let localCurrency = localStorage.getItem("currency-conver-rate");
      let localCurrencyObj = localCurrency ? JSON.parse(localCurrency) : null;
      let obj = {
        userCart: { data: cartItems },
        currencyObj: localCurrencyObj,
      };
      if (localCurrencyObj && localCurrencyObj?.code) {
        setCurrency(localCurrencyObj.code);
      }

      let localDiscount = localStorage.getItem("cart-discount");
      if (localDiscount) {
        try {
          let localDiscountobj = JSON.parse(localDiscount);
          if (localDiscountobj) obj.localDiscount = localDiscountobj;
        } catch (e) {}
      }

      let localShipping = localStorage.getItem("cart-shipping");
      if (localShipping) {
        try {
          let localShippingObj = JSON.parse(localShipping);
          if (localShippingObj) obj.localShipping = localShippingObj;
        } catch (e) {}
      }

      let localAddress = localStorage.getItem("cart-address");
      if (localAddress) {
        try {
          let localAddressObj = JSON.parse(localAddress);
          if (localAddressObj) obj.localAddress = localAddressObj;
        } catch (e) {}
      }

      const res = await createGuestOrder(obj);
      return res;
    } catch (error) {
      errorToast(error);
      return null;
    }
  };

  const clearlocalStorage = () => {
    localStorage.setItem("cart-shipping", null);
    localStorage.setItem("cart-discount", null);
    clearLocalCart();
    setCarts(0);
  };

  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  async function displayRazorpay(obj, orderId, fallbackAmount, fallbackCurrency) {
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const payAmount = (obj && obj.amount) ? obj.amount : (fallbackAmount ? Math.round(Number(fallbackAmount) * 100) : 0);
    const payCurrency = (obj && obj.currency) ? obj.currency : (fallbackCurrency || "INR");

    const options = {
      key: "rzp_live_7vqxfDFU1ZayDE",
      amount: payAmount,
      currency: payCurrency,
      name: "Ahilya",
      description: "Order",
      ...(obj && obj.id ? { order_id: obj.id } : {}),
      handler: async function (response) {
        const serialize = function (o) {
          var str = [];
          for (var p in o)
            if (o && o.hasOwnProperty(p)) {
              str.push(
                encodeURIComponent(p) + "=" + encodeURIComponent(o[p])
              );
            }
          return str.join("&");
        };

        const callbackPayload = (obj && obj.id) ? obj : response;
        let { data: callbackRes } = await orderCallback(
          serialize(callbackPayload),
          orderId
        );
        if (callbackRes) {
          setLoading(false);
          successToast(callbackRes.message);
          clearlocalStorage();
          navigate(`/OrderComplete/${callbackRes.orderId}`);
        }
      },
      theme: {
        color: "#61dafb",
      },
    };
    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  }

  const handleCheckout = async () => {
    try {
      let res = "";
      setLoading(true);
      let localCurrency = localStorage.getItem("currency-conver-rate");
      let localCurrencyObj = localCurrency
        ? JSON.parse(localCurrency)
        : null;
      let obj = {
        currencyObj: localCurrencyObj,
      };
      if (getToken()) {
        res = await createOrder(obj);
      } else {
        res = await handlecreateGuestOrder();
      }
      if (res && res.data && (res.data.success || res.data.orderId)) {
        const currentOrderId = res.data.orderId || res.data.data?.orderId;
        let orderAmount = res.data.convertedTotalAmount || res.data.amount;
        if (!orderAmount && res.data.orderObj) {
          orderAmount =
            res.data.orderObj.convertedTotalAmount ||
            res.data.orderObj.totalAmount;
        }

        if ((!orderAmount || isNaN(orderAmount) || Number(orderAmount) <= 0) && currentOrderId) {
          try {
            const orderDetails = await getOrderById(currentOrderId);
            if (orderDetails?.data?.data) {
              orderAmount =
                orderDetails.data.data.convertedTotalAmount ||
                orderDetails.data.data.totalAmount;
            }
          } catch (e) {}
        }

        displayRazorpay(
          res.data.data,
          currentOrderId,
          orderAmount,
          res.data.currency || currency || "INR"
        );
        setLoading(false);
      } else {
        setLoading(false);
        errorToast(res?.data?.message || "Failed to create order");
      }
    } catch (error) {
      errorToast(error);
      setLoading(false);
    }
  };

  return (
    <div className="shipping-page">
      <div className="container">
        <div className="title-section text-center mb-4">
          <h1 className="heading mb-0">Payment</h1>
          <Link to="/Product-Detail" className="text-dark btn-hover btn-custom">
            Continue Shopping
          </Link>
        </div>
        <div className="row gy-4">
          <div className="col-12 col-xl-7">
            <div className="shipping-page-left">
              <p className="title mb-0">Payment</p>
              <p className="mb-2">All transactions are secure and encrypted.</p>
              <p className="mb-2 text-danger">
                {loading === false
                  ? "Please Click on payment method for Payment."
                  : "Please Wait...."}
              </p>
              <form className="form">
                <ul className="info">
                  <li className="my-2">
                    <div className="form-check">
                      <label className="pointer d-block">
                        <ButtonWrapper
                          currency={currency}
                          setLoading={setLoading}
                          clearlocalStorage={clearlocalStorage}
                          handlecreateGuestOrder={handlecreateGuestOrder}
                        />
                        Paypal (International Payments)
                      </label>
                    </div>
                  </li>
                  <li className="my-2">
                    <div className="form-check">
                      <label
                        className="form-check-label pointer"
                        onClick={() => handleCheckout()}
                      >
                        <img src={images.razorpay_logo} width="100px" alt="Razorpay" />
                        Razorpay Secure (UPI, Cards, Wallets, NetBanking)
                      </label>
                    </div>
                  </li>
                </ul>
              </form>
            </div>
          </div>
          <div className="col-12 col-xl-5">
            <OrderSummary />
          </div>
        </div>
      </div>
    </div>
  );
}

const style = { layout: "vertical" };

const ButtonWrapper = ({
  currency,
  setLoading,
  clearlocalStorage,
  handlecreateGuestOrder,
}) => {
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();
  const orderIdRef = useRef("");
  const navigate = useNavigate();

  const activeCurrency =
    currency && PAYPAL_SUPPORTED_CURRENCIES.includes(currency.toUpperCase())
      ? currency.toUpperCase()
      : "USD";

  useEffect(() => {
    if (options && options.currency !== activeCurrency) {
      dispatch({
        type: "resetOptions",
        value: {
          ...options,
          currency: activeCurrency,
        },
      });
    }
  }, [activeCurrency, options, dispatch]);

  if (isPending) {
    return (
      <div className="py-2 text-center">
        <span className="spinner-border spinner-border-sm text-secondary me-2" role="status" />
        <span className="text-muted small">Loading PayPal...</span>
      </div>
    );
  }

  const scriptCurrency = options.currency || activeCurrency || "USD";

  return (
    <>
      <PayPalButtons
        style={style}
        disabled={false}
        forceReRender={[scriptCurrency, isPending]}
        createOrder={async (data, actions) => {
          try {
            setLoading(true);

            let localCurrency = localStorage.getItem("currency-conver-rate");
            let localCurrencyObj = localCurrency
              ? JSON.parse(localCurrency)
              : null;
            let obj = {
              currencyObj: localCurrencyObj,
            };

            let res;
            if (getToken()) {
              res = await createOrder(obj);
            } else {
              res = await handlecreateGuestOrder();
            }

            if (!res || !res.data || (!res.data.success && !res.data.orderId)) {
              setLoading(false);
              const errMsg = res?.data?.message || "Failed to create order";
              errorToast(errMsg);
              return Promise.reject(new Error(errMsg));
            }

            const currentOrderId = res.data.orderId || res.data.data?.orderId;
            orderIdRef.current = currentOrderId;

            // 1. Check direct amount fields from backend response
            let orderAmount = res.data.convertedTotalAmount || res.data.amount;
            if (!orderAmount && res.data.orderObj) {
              orderAmount =
                res.data.orderObj.convertedTotalAmount ||
                res.data.orderObj.totalAmount;
            }
            if (!orderAmount && res.data.data && typeof res.data.data.amount === "number") {
              orderAmount = res.data.data.amount / 100;
            }

            // 2. Fallback: fetch created order details by ID from backend
            if ((!orderAmount || isNaN(orderAmount) || Number(orderAmount) <= 0) && currentOrderId) {
              try {
                const orderDetails = await getOrderById(currentOrderId);
                if (orderDetails?.data?.data) {
                  orderAmount =
                    orderDetails.data.data.convertedTotalAmount ||
                    orderDetails.data.data.totalAmount;
                }
              } catch (fetchErr) {
                console.warn("Could not fetch order by ID:", fetchErr);
              }
            }

            // 3. Fallback: calculate from client-side cart & exchange rate
            if (!orderAmount || isNaN(orderAmount) || Number(orderAmount) <= 0) {
              try {
                let cartData = [];
                let localCartRes = getLocalCart();
                cartData = localCartRes?.data?.data || [];
                let subtotal = cartData.reduce(
                  (acc, el) =>
                    acc +
                    Number(el.quantity || 1) *
                      Number(el.variantobj?.price || el.price || el.mrp || 0),
                  0
                );
                let discountAmount = 0;
                try {
                  let disc = JSON.parse(localStorage.getItem("cart-discount"));
                  if (disc && disc.amount) discountAmount = Number(disc.amount);
                } catch (e) {}

                let shippingAmount = 0;
                try {
                  let ship = JSON.parse(localStorage.getItem("cart-shipping"));
                  if (ship && ship.price) shippingAmount = Number(ship.price);
                } catch (e) {}

                let totalInr = Math.max(
                  0,
                  subtotal - discountAmount + shippingAmount
                );
                if (localCurrencyObj && localCurrencyObj.convertRate) {
                  orderAmount = Math.round(
                    totalInr * parseFloat(localCurrencyObj.convertRate)
                  );
                } else {
                  orderAmount = totalInr;
                }
              } catch (calcErr) {
                console.warn("Client cart calc error:", calcErr);
              }
            }

            if (!orderAmount || isNaN(orderAmount) || Number(orderAmount) <= 0) {
              setLoading(false);
              const errAmount = "Invalid order amount. Please check your cart.";
              errorToast(errAmount);
              return Promise.reject(new Error(errAmount));
            }

            const formattedValue = Number(orderAmount).toFixed(2);

            console.log("Creating PayPal Order:", {
              orderId: currentOrderId,
              amount: formattedValue,
              currency: scriptCurrency,
            });

            const paypalOrderId = await actions.order.create({
              purchase_units: [
                {
                  amount: {
                    currency_code: scriptCurrency,
                    value: formattedValue,
                  },
                },
              ],
            });

            return paypalOrderId;
          } catch (err) {
            setLoading(false);
            console.error("PayPal createOrder error:", err);
            errorToast(err?.message || "Failed to initiate PayPal payment");
            return Promise.reject(err);
          }
        }}
        onApprove={async (data, actions) => {
          try {
            setLoading(true);
            const details = await actions.order.capture();
            console.log("PayPal capture success:", details);

            const currentOrderId = orderIdRef.current;
            const { data: res } = await paypalPaymentCallback(
              details,
              currentOrderId
            );

            if (res && res.success) {
              setLoading(false);
              successToast(res.message || "Payment completed successfully!");
              clearlocalStorage();
              navigate(`/OrderComplete/${res.orderId}`);
            } else {
              setLoading(false);
              errorToast(res?.message || "Payment confirmation failed");
            }
          } catch (err) {
            setLoading(false);
            console.error("PayPal onApprove error:", err);
            errorToast(err?.message || "Payment capture failed");
          }
        }}
        onError={(err) => {
          setLoading(false);
          console.error("PayPal onError:", err);
          errorToast("PayPal transaction could not be completed. Please try again.");
        }}
        onCancel={() => {
          setLoading(false);
        }}
      />
    </>
  );
};

export default Payment;
